import i18n from 'i18next';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { initReactI18next } from 'react-i18next';

import App from '../Config/App';
import Config from '../Config/Config';

const pageLanguage = document.getElementsByTagName('html')[0].getAttribute('lang') ?? Config.get('app.locale');
const supportedLocales = (Config.get('app.supported_locales') ?? '')
    .split(',')
    .map((locale) => locale.split(':')[0].trim())
    .filter(Boolean);
const preloadLanguages = Array.from(new Set([...supportedLocales, pageLanguage, pageLanguage.split('-')[0], 'en']));
const datepickerLocaleCode = pageLanguage.split('-')[0];

const datepickerLocaleReady = registerDatepickerLocale(datepickerLocaleCode);
const i18nInitialization = initializeI18n();

async function registerDatepickerLocale(locale) {
    setDefaultLocale(locale);

    // EN is a default locale, so we don't need to load translations for it
    if (locale !== 'en') {
        const translations = await import(`date-fns/locale/${locale}/index.js`)
            .then((module) => module?.default)
            // Don't throw because date pickers are not critical
            .catch(() => console.warn(`Datepicker translations for locale ${locale} not found.`));

        if (translations) {
            registerLocale(translations.code, translations);
        }
    }

    return locale;
}

async function initializeI18n() {
    await datepickerLocaleReady;

    await i18n
        .use({
            type: 'backend',
            read: (language, namespace, callback) => {
                import(`../../../lang/${language}/frontend.json`)
                    .then((module) => {
                        callback(null, module?.default);
                    })
                    .catch((error) => {
                        if (!App.isProduction()) {
                            console.warn(`Translation file for ${language}/${namespace} not found.`);
                        }

                        callback(error, false);
                    });
            },
        })
        .use(initReactI18next)
        .init({
            lng: pageLanguage,
            preload: preloadLanguages,
            fallbackLng: (languageCode) => {
                const fallbacks = [];
                if (languageCode) {
                    fallbacks.push(languageCode.split('-')[0]);
                }
                fallbacks.push('en');

                return Array.from(new Set(fallbacks));
            },
            keySeparator: false,
            interpolation: {
                escapeValue: false,
            },
            ns: ['frontend'],
            defaultNS: 'frontend',
            react: {
                // Automatic batching from React 18 is already enabled via createRoot.
                // Suspense stays enabled here only to guard namespace/language loads that happen later.
                // Initial render is protected separately by awaiting i18n initialization before mount.
                useSuspense: true,
            },
        });

    return i18n;
}

await i18nInitialization;

export default i18n;
